// client/src/components/CreateListSheet.tsx
import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiAuthList, apiCreateList, storeToken, clearToken } from '@/api';
import { CODE_RE, toSlugForBackend } from '@/utils/code';

type Props = {
  open: boolean;
  onClose: () => void;
};

export default function CreateListSheet({ open, onClose }: Props) {
  const nav = useNavigate();

  const codeRef = useRef<HTMLInputElement>(null);
  const [code, setCode] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string>('');

  useEffect(() => {
    if (open) {
      setErr('');
      setBusy(false);
      setPassword('');
      setConfirm('');
      setDisplayName('');
      setTimeout(() => codeRef.current?.focus(), 0);
    }
  }, [open]);

  const codeSlug = useMemo(() => toSlugForBackend(code), [code]);

  function validateInputs(): string | null {
    const raw = (code || '').trim();
    if (!CODE_RE.test(raw)) return 'Code must be 4–12 letters or digits.';
    if (!codeSlug) return 'Code becomes empty after sanitizing.';
    if (!password || password.trim().length < 4) return 'Password must be at least 4 characters.';
    if (password !== confirm) return 'Passwords do not match.';
    return null;
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr('');
    const v = validateInputs();
    if (v) { setErr(v); return; }

    try {
      setBusy(true);

      await apiCreateList({
        code: codeSlug,
        displayName: displayName.trim() || undefined,
        password: password
      });

      // authenticate immediately
      clearToken();
      const { token } = await apiAuthList(codeSlug, password);
      storeToken(token);

      onClose();
      nav(`/${encodeURIComponent(codeSlug)}`);
    } catch (e: any) {
      let m = 'Failed to create calendar.';
      if (typeof e?.message === 'string') {
        try {
          const obj = JSON.parse(e.message);
          if (typeof obj === 'string') m = obj;
          else if (obj?.message) m = String(obj.message);
          else if (obj?.code) m = String(obj.code);
        } catch { m = e.message; }
      }
      if (/409|exists|already/i.test(m)) m = 'That code is taken.';
      if (/password/i.test(m)) m = 'Password must be at least 4 characters.';
      setErr(m);
    } finally {
      setBusy(false);
    }
  }

  if (!open) return null;

  return (
    <div className="sheet" role="dialog" aria-modal="true" aria-labelledby="create-title">
      <div className="backdrop" onClick={onClose} aria-hidden="true"></div>

      <form className="panel glass" onSubmit={submit}>
        <h3 id="create-title">Create calendar</h3>

        <div className="field" style={{ marginTop: 10 }}>
          <label htmlFor="create-code">Code</label>
          <input
            id="create-code"
            ref={codeRef}
            type="text"
            inputMode="text"
            autoComplete="off"
            placeholder="e.g., DNB2025"
            required
            maxLength={12}
            value={code}
            onChange={(e)=>setCode(e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="create-name">Display name</label>
          <input
            id="create-name"
            type="text"
            placeholder="Group name"
            value={displayName}
            onChange={(e)=>setDisplayName(e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="create-password">Password (required)</label>
          <input
            id="create-password"
            type="password"
            placeholder="••••"
            minLength={4}
            required
            value={password}
            onChange={(e)=>setPassword(e.target.value)}
            autoComplete="new-password"
          />
        </div>

        <div className="field">
          <label htmlFor="create-confirm">Confirm password</label>
          <input
            id="create-confirm"
            type="password"
            placeholder="••••"
            required
            value={confirm}
            onChange={(e)=>setConfirm(e.target.value)}
            autoComplete="new-password"
          />
        </div>

        <div className="error" role="alert" aria-live="polite" style={{ minHeight: '1.25em' }}>
          {err}
        </div>

        <div className="actions" style={{ marginTop: 8 }}>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn primary" disabled={busy}>
            {busy ? 'Creating…' : 'Create'}
          </button>
        </div>
      </form>
    </div>
  );
}
